import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'

import { useSyncStatusFilter } from 'modules/integrations/public'

import {
  approvedAtFilter,
  createCategoryAsyncFilter,
  createCurrencyAsyncFilter,
  createEmployeeIdsAsyncFilter,
  createLegalEntityIdsAsyncFilter,
  createReimbursableCurrencyAsyncFilter,
  createSubcategoryAsyncFilter,
  creationTypeFilter,
  documentTypeFilter,
  groupedFilter,
  internalReferenceFilter,
  pendingApprovalFilter,
  reimbursableFilter,
  reportDateFilter,
  spendingAlertTypeFilter,
  statusFilter,
} from './filters'
import { useProjectFilter } from './filters/useProjectFilter'

interface UseExpensesFiltersProps {
  companyId: string
  isGroupView?: boolean
  recommendationsEnabled?: boolean
  integrationsFrameworkExpensesSyncFeatureAvailable: boolean
}

/**
 * Filter types:
 * - Simple filters: Use factory functions, no async data fetching
 * - Async filters: Fetch data on-demand from GraphQL, cached by Apollo Client
 */
export const useExpensesFilters = ({
  companyId,
  isGroupView,
  recommendationsEnabled,
  integrationsFrameworkExpensesSyncFeatureAvailable,
}: UseExpensesFiltersProps) => {
  const apolloClient = useApolloClient()
  const syncStatusFilter = useSyncStatusFilter()
  const showsSyncStatusFilter = !isGroupView && integrationsFrameworkExpensesSyncFeatureAvailable
  const projectFilter = useProjectFilter()

  return useMemo(
    () => ({
      spendingAlertType: spendingAlertTypeFilter(recommendationsEnabled), // alerts
      approvedAt: approvedAtFilter(), // approval date
      category: createCategoryAsyncFilter(apolloClient, companyId), // category
      reviewRequestAt: reportDateFilter(), // creation date
      currency: createCurrencyAsyncFilter(apolloClient, companyId), // currency
      documentType: documentTypeFilter(), // document type
      creationType: creationTypeFilter(), // expense type
      ...(!isGroupView ? { grouped: groupedFilter() } : {}), // grouped
      internalReference: internalReferenceFilter(), // internal reference
      employeeIds: createEmployeeIdsAsyncFilter(apolloClient, companyId), // owner
      legalEntityIds: createLegalEntityIdsAsyncFilter(apolloClient), // owner legal entity
      reimbursable: reimbursableFilter(), // payment
      pendingApproval: pendingApprovalFilter(), // pending approval
      project: projectFilter, // project
      reimbursableCurrency: createReimbursableCurrencyAsyncFilter(apolloClient, companyId), // reimbursable currency
      status: statusFilter(), // status
      subcategory: createSubcategoryAsyncFilter(apolloClient, companyId), // subcategory
      ...(showsSyncStatusFilter ? syncStatusFilter : {}),
    }),
    [
      apolloClient,
      companyId,
      isGroupView,
      projectFilter,
      recommendationsEnabled,
      syncStatusFilter,
      showsSyncStatusFilter,
    ]
  )
}
